/**
 * Sale Order Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as saleService from '../api/services/sale.service';
import { toast } from '../stores/toast.store';

// Query keys
const KEYS = {
  orders: ['sale', 'orders'] as const,
  order: (id: number) => ['sale', 'orders', id] as const,
  commission: (id: number) => ['sale', 'orders', id, 'commission'] as const,
  signedWithoutDeposit: (params: object) => ['sale', 'signed-without-deposit', params] as const,
};

interface UseOrdersParams {
  offset?: number;
  limit?: number;
  state?: string;
  partnerId?: number;
  userId?: number;
  search?: string;
  myOrders?: boolean;
  createdByMe?: boolean;
  dateFrom?: string;
  dateTo?: string;
  payDateFrom?: string;
  payDateTo?: string;
}

/**
 * Hook for fetching sale orders list
 */
export function useOrders(params: UseOrdersParams = {}) {
  return useQuery({
    queryKey: [...KEYS.orders, params],
    queryFn: async () => {
      const result = await saleService.getSaleOrders({
        offset: params.offset,
        limit: params.limit,
        state: params.state,
        partner_id: params.partnerId,
        user_id: params.userId,
        search: params.search,
        my_orders: params.myOrders,
        created_by_me: params.createdByMe,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        pay_date_from: params.payDateFrom,
        pay_date_to: params.payDateTo,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch orders');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single sale order
 */
export function useOrder(orderId: number | undefined) {
  return useQuery({
    queryKey: KEYS.order(orderId!),
    queryFn: async () => {
      const result = await saleService.getSaleOrder(orderId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch order');
      }
      return result.data!;
    },
    enabled: !!orderId,
  });
}

/**
 * Hook for fetching order commission data
 */
export function useOrderCommission(orderId: number | undefined) {
  return useQuery({
    queryKey: KEYS.commission(orderId!),
    queryFn: async () => {
      const result = await saleService.getOrderCommission(orderId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch commission');
      }
      return result.data!;
    },
    enabled: !!orderId,
  });
}

/**
 * Hook for confirming order
 */
export function useConfirmOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: number) => {
      const result = await saleService.confirmOrder(orderId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to confirm order');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.orders });
      queryClient.invalidateQueries({ queryKey: KEYS.order(data.id) });
      toast.success('訂單已確認');
    },
    onError: (error: Error) => {
      toast.error('確認失敗', error.message);
    },
  });
}

/**
 * Hook for getting quote link
 */
export function useGetQuoteLink() {
  return useMutation({
    mutationFn: async (orderId: number) => {
      const result = await saleService.getQuoteLink(orderId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to generate quote link');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      // Copy to clipboard
      if (navigator.clipboard) {
        navigator.clipboard.writeText(data.quote_url);
        toast.success('連結已複製', '報價單連結已複製到剪貼簿');
      }
    },
    onError: (error: Error) => {
      toast.error('產生連結失敗', error.message);
    },
  });
}

interface UseSignedWithoutDepositParams {
  offset?: number;
  limit?: number;
}

/**
 * Hook for listing signed orders without deposit invoice
 */
export function useSignedWithoutDeposit(params: UseSignedWithoutDepositParams = {}) {
  return useQuery({
    queryKey: KEYS.signedWithoutDeposit(params),
    queryFn: async () => {
      const result = await saleService.getSignedWithoutDeposit(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch orders');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for creating deposit invoice
 */
export function useCreateDepositInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      percentage,
      itemName,
    }: {
      orderId: number;
      percentage: number;
      itemName?: string;
    }) => {
      const result = await saleService.createDepositInvoice(orderId, percentage, itemName);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create deposit invoice');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sale', 'signed-without-deposit'] });
      queryClient.invalidateQueries({ queryKey: KEYS.orders });
      if (data.einvoice_error) {
        toast.warning(
          '訂金憑單已建立，電子發票開立失敗',
          `${data.einvoice_error}，請聯繫會計`,
        );
      } else if (data.einvoice_skipped_reason) {
        // 依公司政策沒開票（例如玉禮）：不是錯誤，用中性語氣，但一定要說，
        // 否則業務會以為電子發票已經開出去了。
        toast.success('訂金憑單已建立', data.einvoice_skipped_reason);
      } else {
        toast.success('預收款發票已建立');
      }
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for fetching e-invoice PDF URL on demand
 */
export function useGetEinvoicePdf() {
  return useMutation({
    mutationFn: async (orderId: number) => {
      const result = await saleService.getEinvoicePdf(orderId);
      if (!result.success) {
        throw new Error(result.error?.message || '取得電子發票 PDF 失敗');
      }
      return result.data!;
    },
    onError: (error: Error) => {
      toast.error('取得失敗', error.message);
    },
  });
}

/**
 * Hook for generating deposit package PDF link (order + invoice + e-invoice)
 */
export function useGetDepositPackageLink() {
  return useMutation({
    mutationFn: async (orderId: number) => {
      const result = await saleService.getDepositPackageLink(orderId);
      if (!result.success) {
        throw new Error(result.error?.message || '產生請款連結失敗');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      navigator.clipboard.writeText(data.pdf_url).catch(() => {});
      toast.success('請款連結已複製', '連結已複製到剪貼簿，可直接傳給客戶');
    },
    onError: (error: Error) => {
      toast.error('產生連結失敗', error.message);
    },
  });
}
